/* eslint-disable consistent-return */
import { useTranslation } from 'react-i18next'
import {
  Dispatch,
  SetStateAction,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react'
import styles from './TableCardsList.module.scss'
import TextField from '@/shared/components/TextField/TextField'
import CheckBox from '@/shared/components/CheckBox/CheckBox'
import IconButton from '@/shared/components/IconButton/IconButton'
import { useTableCardsQuery } from '../../hooks/useTableCardsQuery'
import { usePlannerTables } from '../../hooks/usePlannerTables'
import TableCardPreviewTile from '@/shared/components/TableCardPreviewTile/TableCardPreviewTile'
import { useTableCardStore } from '@/features/table-cards/store/useTableCardStore'
import { ExtendedQrCodeCardModel } from '../CardsPrintLayout/CardsPrintLayout'

interface TableCardsListProps {
  setIsEditing: Dispatch<SetStateAction<boolean>>
  selectedRowIds: string[]
  openModal: () => void
  setSelectedCards: Dispatch<SetStateAction<ExtendedQrCodeCardModel[]>>
  setIsPrintGridModalOpen: Dispatch<SetStateAction<boolean>>
  setSelectedTableCards: Dispatch<SetStateAction<string[]>>
  selectedTableCards: string[]
}

function TableCardsList(props: TableCardsListProps) {
  const {
    setIsEditing,
    selectedRowIds,
    openModal,
    setSelectedCards,
    setIsPrintGridModalOpen,
    setSelectedTableCards,
    selectedTableCards
  } = props

  const { tableCardsList, deleteTableCards } = useTableCardsQuery()
  const plannerTables = usePlannerTables()
  const containerRef = useRef<HTMLDivElement | null>(null)

  const [filteredTableCards, setFilteredTableCards] =
    useState<typeof tableCardsList>(tableCardsList)

  const { t } = useTranslation()

  const [isScrollable, setIsScrollable] = useState(false)

  const intermediate =
    selectedTableCards.length > 0 &&
    selectedTableCards.length < (tableCardsList?.length || 0)

  const selectAllTableCards = () => {
    if (selectedTableCards.length === tableCardsList?.length || intermediate) {
      setSelectedTableCards([])
    } else {
      const allIds = tableCardsList?.map(card => card.id) || []
      setSelectedTableCards(allIds)
    }
  }

  useEffect(() => {
    const container = containerRef.current
    if (!container) return

    const checkScroll = () => {
      setIsScrollable(container.scrollHeight > container.clientHeight)
    }

    checkScroll()

    const observer = new ResizeObserver(() => checkScroll())
    observer.observe(container)

    return () => {
      observer.disconnect()
    }
  }, [tableCardsList])

  useEffect(() => {
    setFilteredTableCards(tableCardsList)
  }, [tableCardsList])

  const handleOnSearchChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const searchTerm = event.target.value.toLowerCase()
    const filteredCards = tableCardsList?.filter(card =>
      card.title.toLowerCase().includes(searchTerm)
    )
    setFilteredTableCards(filteredCards)
  }

  const handleOnAddClick = () => {
    useTableCardStore.getState().resetToInitialState()
    setIsEditing(false)
    openModal()
  }

  const checkIfIsSelectable = (id: string) => {
    if (
      selectedTableCards.length === 1 &&
      !selectedTableCards.includes(id) &&
      selectedRowIds.length !== 0
    ) {
      return false
    }
    return true
  }

  const selectedCards = useMemo(() => {
    if (selectedTableCards.length === 1) {
      const card = tableCardsList?.find(
        card => card.id === selectedTableCards[0]
      )
      // Only planner tables with a matching backend QR can actually be printed.
      const tables = plannerTables.filter(
        table => selectedRowIds?.includes(table.id) && table.hasQr
      )

      if (card && tables.length) {
        return tables.map(table => ({
          ...card,
          tableNumber: table.number,
          tableId: table.tableId
        }))
      }
    }
    return []
  }, [selectedRowIds, selectedTableCards, tableCardsList, plannerTables])

  useEffect(() => {
    setSelectedCards(selectedCards)
  }, [selectedCards, setSelectedCards])

  const handleOnPrintClick = () => {
    setIsPrintGridModalOpen(true)
  }

  return (
    <div className={styles.tableCardsListWrapper}>
      <div className={styles.tableCardsHeader}>
        <TextField
          placeholder={t('TABLE.GRID_TOOLBAR.SEARCH')}
          maxWidth="15rem"
          onChange={handleOnSearchChange}
        />
        <div className={styles.tableCardsHeaderHeaderContainer}>
          <CheckBox
            onChange={selectAllTableCards}
            intermediate={intermediate}
            checked={selectedTableCards.length === tableCardsList?.length}
            disabled={
              selectedRowIds.length > 0 && selectedTableCards.length === 1
            }
          />
          <IconButton
            icon="las la-plus icon-big"
            color="primary"
            onClick={handleOnAddClick}
            disabled={
              selectedRowIds.length > 0 && selectedTableCards.length === 1
            }
          />
          <IconButton
            icon="las la-print icon-big"
            color="primary"
            onClick={handleOnPrintClick}
            disabled={
              selectedRowIds.length === 0 || selectedTableCards.length !== 1
            }
          />

          <IconButton
            icon="las la-trash icon-big"
            color="primary"
            onClick={() => deleteTableCards(selectedTableCards)}
            disabled={
              (selectedRowIds.length > 0 && selectedTableCards.length === 1) ||
              selectedTableCards.length === 0
            }
          />
        </div>
      </div>
      <div className={styles.tableCardsWrapper}>
        <div
          className={`${styles.tableCardsContainer} ${
            isScrollable ? styles.scrollable : ''
          }`}
          ref={containerRef}
        >
          {filteredTableCards?.map(qrCard => (
            <TableCardPreviewTile
              key={qrCard.id}
              qrCard={qrCard}
              selectedTableCards={selectedTableCards}
              setSelectedTableCards={setSelectedTableCards}
              setIsEditing={setIsEditing}
              openModal={openModal}
              disableSelection={!checkIfIsSelectable(qrCard.id)}
            />
          ))}
        </div>
      </div>
    </div>
  )
}

export default TableCardsList
